import React, { useEffect, useState, useCallback } from "react";
import { apiInstanceFetch } from "../../../util/api";

// Every real-money payment taken by the platform. Before this the only copy
// lived in the Paystack dashboard.
const PURPOSES = ["All", "coins", "order", "subscription", "other"];
const METHODS = ["All", "mobile_money", "card", "bank", "cash", "ussd"];

const money = (n) =>
  "$" + (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const methodLabel = (m) => {
  if (!m) return "—";
  if (m === "mobile_money") return "Mobile money";
  if (m === "card") return "Card";
  if (m === "cash") return "Cash";
  return m.charAt(0).toUpperCase() + m.slice(1);
};

const methodChip = (m) => {
  const map = {
    mobile_money: { bg: "#E8F5E9", fg: "#1B7F3B" },
    card: { bg: "#E8EEFF", fg: "#2545B8" },
    cash: { bg: "#FFF4E0", fg: "#9A6207" },
    bank: { bg: "#EDE9FE", fg: "#5B3FBF" },
  };
  return map[m] || { bg: "#F1F1F1", fg: "#6B7280" };
};

const purposeChip = (p) => {
  const map = {
    coins: { bg: "#FFF8E1", fg: "#B4830E", label: "Coins" },
    order: { bg: "#E8F5E9", fg: "#1B7F3B", label: "Product order" },
    subscription: { bg: "#EDE9FE", fg: "#5B3FBF", label: "Subscription" },
  };
  return map[p] || { bg: "#F1F1F1", fg: "#6B7280", label: p || "Other" };
};

const Payments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalUsd, setTotalUsd] = useState(0);
  const [byPurpose, setByPurpose] = useState([]);
  const [byMethod, setByMethod] = useState([]);

  const [purpose, setPurpose] = useState("All");
  const [method, setMethod] = useState("All");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page, limit, purpose, method,
        ...(search ? { search } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      }).toString();
      const res = await apiInstanceFetch.get(`paymentLedger/adminList?${qs}`);
      if (res?.status) {
        setRows(res.data || []);
        setTotal(res.total || 0);
        setTotalUsd(res.totalUsd || 0);
        setByPurpose(res.byPurpose || []);
        setByMethod(res.byMethod || []);
      }
    } catch (e) {
      /* keep last good view */
    } finally {
      setLoading(false);
    }
  }, [page, limit, purpose, method, search, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(Math.ceil(total / limit), 1);
  const pill = (label, value, sub) => (
    <div style={{ flex: 1, minWidth: 170, background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize: 12.5, color: "#8a8a8a", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "#1A1614", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: "#9a9a9a", marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const inputStyle = {
    padding: "9px 12px", border: "1px solid #e3e3e3", borderRadius: 8, fontSize: 14, outline: "none",
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A1614" }}>Payments</h4>
        <div style={{ fontSize: 13, color: "#8a8a8a" }}>
          Every real-money payment received — coins, product orders and more. Coin-funded activity (gifts, ads, subscriptions) is not listed here, that money is already counted at the coin purchase.
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        {pill("Total received", money(totalUsd), `${total} payment${total === 1 ? "" : "s"}`)}
        {byPurpose.map((p) => {
          const c = purposeChip(p._id);
          return <div key={p._id}>{pill(c.label, money(p.usd), `${p.n} payment${p.n === 1 ? "" : "s"}`)}</div>;
        })}
        {byMethod.filter((m) => m._id).slice(0, 2).map((m) => (
          <div key={m._id}>{pill(methodLabel(m._id), money(m.usd), `${m.n} payment${m.n === 1 ? "" : "s"}`)}</div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
        <input
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder="Search name, email, reference…"
          style={{ ...inputStyle, minWidth: 250, flex: 1 }}
        />
        <select value={purpose} onChange={(e) => { setPage(1); setPurpose(e.target.value); }} style={inputStyle}>
          {PURPOSES.map((p) => <option key={p} value={p}>{p === "All" ? "All types" : purposeChip(p).label}</option>)}
        </select>
        <select value={method} onChange={(e) => { setPage(1); setMethod(e.target.value); }} style={inputStyle}>
          {METHODS.map((m) => <option key={m} value={m}>{m === "All" ? "All methods" : methodLabel(m)}</option>)}
        </select>
        <input type="date" value={startDate} onChange={(e) => { setPage(1); setStartDate(e.target.value); }} style={inputStyle} />
        <input type="date" value={endDate} onChange={(e) => { setPage(1); setEndDate(e.target.value); }} style={inputStyle} />
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr style={{ background: "#FAF7F0" }}>
                {["Date & time", "Who paid", "What for", "Amount", "Method", "Reference"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 14px", fontSize: 12, color: "#6b7280", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#9a9a9a" }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: "#9a9a9a" }}>No payments found</td></tr>
              ) : rows.map((r) => {
                const pc = purposeChip(r.purpose);
                const mc = methodChip(r.method);
                return (
                  <tr key={r._id} style={{ borderTop: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px 14px", fontSize: 13, whiteSpace: "nowrap", color: "#374151" }}>{when(r.paidAt)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>
                      <div style={{ fontWeight: 600, color: "#1A1614" }}>{r.userName || "—"}</div>
                      <div style={{ fontSize: 11.5, color: "#9a9a9a" }}>{r.userEmail || ""}</div>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>
                      <span style={{ background: pc.bg, color: pc.fg, padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{pc.label}</span>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{r.description || ""}</div>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 13, whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 700, color: "#1A1614" }}>{r.currency} {Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      {r.currency !== "USD" && <div style={{ fontSize: 11.5, color: "#9a9a9a" }}>≈ {money(r.amountUsd)}</div>}
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 13 }}>
                      <span style={{ background: mc.bg, color: mc.fg, padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{methodLabel(r.method)}</span>
                      <div style={{ fontSize: 11.5, color: "#9a9a9a", marginTop: 3 }}>{r.methodDetail || r.provider || ""}</div>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 11.5, color: "#6b7280", maxWidth: 190, wordBreak: "break-all" }}>{r.reference || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderTop: "1px solid #f0f0f0", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12.5, color: "#8a8a8a" }}>
            {total === 0 ? "0" : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)}`} of {total}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["«", 1, page === 1], ["‹ Prev", page - 1, page === 1],
              [`Page ${page} of ${pages}`, null, true],
              ["Next ›", page + 1, page >= pages], ["»", pages, page >= pages]].map(([label, target, disabled], i) => (
              <button
                key={i}
                onClick={() => target && setPage(target)}
                disabled={disabled}
                style={{
                  padding: "6px 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
                  border: "1px solid " + (disabled ? "#eee" : "#F0C420"),
                  background: disabled ? "#fafafa" : "#FFF8E1",
                  color: disabled ? "#bbb" : "#1A1614",
                  cursor: disabled || !target ? "default" : "pointer",
                }}
              >{label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;
